// PaymentNewDialog — ручное создание платежа (V12 U-35, волна R66).
// POST /api/payments {amount, currency, paymentDate?, fiscalDocNumber?, entityType+entityId}
// → backend ставит source=MANUAL, status=PENDING, currency=RUB (54-ФЗ).
// Используется: список платежей (FAB «Создать платёж»), блок сущности (preset привязки),
// после 422 загрузки чека («Создать вручную» с preset).

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { paymentsApi, type Payment } from '@/api/payments';
import { EntityBindingSelect, type PaymentBinding } from './EntityBindingSelect';
import { parseAmount } from '@/lib/money';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Валюты платежа (54-ФЗ: RUB по умолчанию; остальные — из канона сделок). */
const CURRENCIES = ['RUB', 'USD', 'EUR', 'CNY'] as const;

interface PaymentNewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Предзаполненная привязка (блок карточки / 422-чек → «Создать вручную»). */
  preset?: PaymentBinding | null;
  /** Колбэк успешного создания (например, навигация/бейдж). */
  onCreated?: (payment: Payment) => void;
}

/** Диалог ручного создания платежа (source=MANUAL; обязательны сумма и привязка). */
export function PaymentNewDialog({ open, onClose, preset = null, onCreated }: PaymentNewDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<string>('RUB');
  const [paymentDate, setPaymentDate] = useState('');
  const [fiscalDocNumber, setFiscalDocNumber] = useState('');
  const [binding, setBinding] = useState<PaymentBinding | null>(preset);

  // Сброс формы под диалог: preset (из карточки/422) при каждом открытии (динамический preset).
  useEffect(() => {
    if (open) {
      setBinding(preset);
      setAmount('');
      setPaymentDate('');
      setFiscalDocNumber('');
    }
  }, [open, preset]);

  const parsedAmount = parseAmount(amount);
  const canSubmit = parsedAmount !== null && parsedAmount > 0 && Boolean(binding?.entityId);

  const createMutation = useMutation({
    mutationFn: () => {
      if (!binding || parsedAmount === null) throw new Error('Привязка и сумма обязательны');
      return paymentsApi.create({
        amount: parsedAmount,
        currency,
        ...(paymentDate ? { paymentDate: new Date(paymentDate).toISOString() } : {}),
        ...(fiscalDocNumber.trim() ? { fiscalDocNumber: fiscalDocNumber.trim() } : {}),
        entityType: binding.entityType,
        entityId: binding.entityId,
      });
    },
    onSuccess: (payment) => {
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
      onCreated?.(payment);
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('payments.create.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {createMutation.isError ? (
            <Alert severity="error">{extractApiErrorMessage(createMutation.error, t('payments.create.error'))}</Alert>
          ) : null}
          <TextField
            label={t('payments.amount')}
            required
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ 'aria-label': t('payments.amount') }}
          />
          <TextField
            select
            label={t('payments.currency')}
            fullWidth
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            inputProps={{ 'aria-label': t('payments.currency') }}
          >
            {CURRENCIES.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('payments.paymentDate')}
            type="datetime-local"
            fullWidth
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            inputProps={{ 'aria-label': t('payments.paymentDate') }}
          />
          <TextField
            label={t('payments.fiscalDocNumber')}
            fullWidth
            value={fiscalDocNumber}
            onChange={(e) => setFiscalDocNumber(e.target.value)}
            inputProps={{ 'aria-label': t('payments.fiscalDocNumber') }}
          />
          <EntityBindingSelect value={binding} onChange={setBinding} locked={Boolean(preset)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => createMutation.mutate()}
          disabled={createMutation.isPending || !canSubmit}
        >
          {t('payments.create.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
